import { createSlice } from "@reduxjs/toolkit";

// Initial state
const initialState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
  loginData: null,
  securityGroups: [],
  selectedSecurityGroup: null,
  previousSecurityGroup: null, // stores the admin security group before switching to an outlet
};

// Slice
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // Save access token
    saveAccessToken: (state, action) => {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      state.isAuthenticated = true;
      if (refreshToken) {
        state.refreshToken = refreshToken;
      }
    },

    // Save login data
    saveLoginData: (state, action) => {
      state.loginData = action.payload;
    },

    saveSecurityGroups: (state, action) => {
      state.securityGroups = action.payload;
    },

    saveSelectedSecurityGroup: (state, action) => {
      state.selectedSecurityGroup = action.payload;
    },

    // Called when a super admin selects an outlet from the outlets listing:
    // - saves the current selectedSecurityGroup into previousSecurityGroup so it can be restored later
    // - replaces selectedSecurityGroup with the outlet's security group
    // - sets superUser to false so coreService sends is-Super-Admin: false in request headers
    switchToOutletSecurityGroup: (state, action) => {
      state.previousSecurityGroup = state.selectedSecurityGroup;
      state.selectedSecurityGroup = action.payload;
      state.loginData = { ...state.loginData, superUser: false };
    },

    // Called when the admin clicks the reset button in the navbar:
    // - restores the previously saved admin security group back to selectedSecurityGroup
    // - clears previousSecurityGroup
    // - sets superUser back to true so coreService sends is-Super-Admin: true in request headers
    resetToAdminSecurityGroup: (state) => {
      state.selectedSecurityGroup = state.previousSecurityGroup;
      state.previousSecurityGroup = null;
      state.loginData = { ...state.loginData, superUser: true };
    },

    // Delete access token (logout)
    deleteAccessToken: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.isAuthenticated = false;
      state.loginData = null;
      state.securityGroups = [];
      state.selectedSecurityGroup = null;
      state.previousSecurityGroup = null;
    },

    // Update user information
    setUser: (state, action) => {
      state.user = action.payload;
    },

    // Clear user information
    clearUser: (state) => {
      state.user = null;
    },
  },
});

// Export actions
export const {
  saveAccessToken,
  deleteAccessToken,
  setUser,
  clearUser,
  saveLoginData,
  saveSecurityGroups,
  saveSelectedSecurityGroup,
  switchToOutletSecurityGroup,
  resetToAdminSecurityGroup,
} = userSlice.actions;

// Export selectors
export const selectAccessToken = (state) => state.user?.accessToken;
export const selectRefreshToken = (state) => state.user?.refreshToken;
export const selectUser = (state) => state.user?.user;
export const selectSuperUser = (state) => state.user?.loginData?.superUser;
export const selectIsAuthenticated = (state) => !!state.user?.accessToken;
export const selectLoginData = (state) => state.user?.loginData;
export const selectSecurityGroups = (state) => state.user?.securityGroups;
export const selectSelectedSecurityGroup = (state) =>
  state.user?.selectedSecurityGroup;
export const selectPreviousSecurityGroup = (state) =>
  state.user?.previousSecurityGroup;
// Export reducer
export default userSlice.reducer;
